import React, { useCallback, useRef } from 'react';
import { styled } from '@mui/material/styles';
import { Typography } from '@mui/material';
import { TipTapEditor } from '../TipTap';
import { NODE_UI, stopPropagation } from './nodeUiStyles';

/**
 * NodeRichTextEditor — Full TipTap editor with formatting toolbar.
 * Used for email body, document content, and other rich text fields.
 *
 * This is the inline equivalent of TextEditor/DocumentEditor from the panel templates.
 * Includes: bold, italic, underline, lists, links, tables, images, alignment, undo/redo.
 */

const Label = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.label,
  fontWeight: NODE_UI.fontWeight.label,
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  marginBottom: '4px',
  userSelect: 'none',
}));

const EditorWrapper = styled('div')(({ theme }) => ({
  width: '100%',
  // Single border on the TipTap container — no extra wrapper borders
  '& .tiptap-editor-container': {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: NODE_UI.borderRadius,
    overflow: 'hidden',
    backgroundColor: theme.palette.background.default,
  },
  '& .tiptap-editor-container:focus-within': {
    borderColor: theme.palette.primary.main,
    borderWidth: '1px',
    boxShadow: 'none',
  },
  // Override bloated base TipTap styles — chain .tiptap-minimal for specificity 0,4,0 to beat the CSS file's 0,3,0
  '& .tiptap-editor-container.tiptap-minimal .tiptap-editor-content': {
    padding: '0 !important',
    minHeight: 'auto !important',
  },
  '& .tiptap-editor-container.tiptap-minimal .tiptap-editor-content .ProseMirror': {
    fontSize: NODE_UI.fontSize.value,
    minHeight: '120px',
    padding: '8px 12px !important',
    outline: 'none',
    border: 'none',
    '& p': { margin: 0 },
  },
  // Also override for non-minimal variants
  '& .tiptap-editor-container .tiptap-editor-content': {
    padding: '0 !important',
    minHeight: 'auto !important',
  },
  '& .tiptap-editor-container .tiptap-editor-content .ProseMirror': {
    fontSize: NODE_UI.fontSize.value,
    minHeight: '120px',
    padding: '8px 12px',
    outline: 'none',
    border: 'none',
    '& p': { margin: 0 },
  },
  '&.node-rte-error .tiptap-editor-container': {
    borderColor: theme.palette.error.main,
  },
}));

const NodeRichTextEditor = React.memo(function NodeRichTextEditor({
  label,
  value = '',
  onChange,
  onBlur,
  availablePlaceholders = [],
  placeholder = 'Enter content...',
  error = false,
  minHeight = '120px',
  variant = 'default',
  markdown = false,
  sx,
}) {
  const editorRef = useRef(null);

  const handleChange = useCallback((newValue) => {
    onChange?.(newValue);
  }, [onChange]);

  const handleBlur = useCallback((newValue) => {
    onBlur?.(newValue);
  }, [onBlur]);

  return (
    <div
      onMouseDown={stopPropagation}
      onClick={stopPropagation}
      onPointerDown={stopPropagation}
      onWheel={stopPropagation}
    >
      {label && <Label>{label}</Label>}
      <EditorWrapper className={error ? 'node-rte-error' : ''} sx={sx}>
        <TipTapEditor
          ref={editorRef}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          showToolbar={true}
          stripHtml={false}
          triggerChars={['$']}
          availablePlaceholders={availablePlaceholders}
          placeholder={placeholder}
          minHeight={minHeight}
          variant={variant}
          markdown={markdown}
        />
      </EditorWrapper>
    </div>
  );
});

export default NodeRichTextEditor;
